import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/apiUtils';

interface RegionalHistoryItem {
  id: string;
  document_type: 'CMA' | 'Marketing Plan' | 'Loom Report';
  agent_name: string;
  agent_email: string;
  market_center_name: string | null;
  property_address: string | null;
  seller_name: string | null;
  seller_email: string | null;
  seller_phone: string | null;
  file_name: string;
  file_url: string;
  created_at: string;
}

type DateFilter = 'ALL' | 'THIS_MONTH' | 'LAST_30_DAYS' | 'CUSTOM';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function TypePill({ type }: { type: RegionalHistoryItem['document_type'] }) {
  const palette =
    type === 'CMA'
      ? { bg: 'var(--brand-soft)', fg: 'var(--brand)' }
      : type === 'Marketing Plan'
      ? { bg: '#E8F3FF', fg: '#005A9C' }
      : { bg: '#F3F4F6', fg: '#334155' };

  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: palette.bg, color: palette.fg }}
    >
      {type}
    </span>
  );
}

export default function AIToolsRegionalAdminHistoryPage() {
  const { token, activeContext, isRegionalAdmin } = useAuth();
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'CMA' | 'Marketing Plan' | 'Loom Report'>('ALL');
  const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const authHeaders = {
    Authorization: `Bearer ${token ?? ''}`,
    'X-Active-Context': activeContext?.id ?? '',
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-tools-regional-history', token, activeContext?.id],
    queryFn: async () => {
      const res = await apiFetch('/api/ai-tools/regional-admin-history', {
        headers: authHeaders,
      });
      const body = await res.json().catch(() => ({})) as { items?: RegionalHistoryItem[]; error?: string };
      if (!res.ok) {
        throw new Error(body.error ?? 'Failed to load Regional Admin AI Tools history.');
      }
      return body.items ?? [];
    },
    enabled: Boolean(token) && Boolean(isRegionalAdmin),
  });

  const totals = useMemo(() => {
    const items = data ?? [];
    const cmaCount = items.filter((row) => row.document_type === 'CMA').length;
    const marketingCount = items.filter((row) => row.document_type === 'Marketing Plan').length;
    return {
      all: cmaCount + marketingCount,
      cma: cmaCount,
      marketing: marketingCount,
      loom: items.filter((row) => row.document_type === 'Loom Report').length,
      uniqueAgents: new Set(items.map((row) => row.agent_email.trim().toLowerCase()).filter(Boolean)).size,
    };
  }, [data]);

  const filteredRows = useMemo(() => {
    const items = data ?? [];
    const now = new Date();
    const startThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startLast30Days = new Date(now);
    startLast30Days.setDate(now.getDate() - 30);

    return items.filter((row) => {
      if (typeFilter !== 'ALL' && row.document_type !== typeFilter) {
        return false;
      }

      const created = new Date(row.created_at);
      if (Number.isNaN(created.getTime())) {
        return false;
      }

      if (dateFilter === 'THIS_MONTH') {
        return created >= startThisMonth;
      }

      if (dateFilter === 'LAST_30_DAYS') {
        return created >= startLast30Days;
      }

      if (dateFilter === 'CUSTOM') {
        const createdYmd = toYmd(created);
        if (fromDate && createdYmd < fromDate) return false;
        if (toDate && createdYmd > toDate) return false;
      }

      return true;
    });
  }, [data, typeFilter, dateFilter, fromDate, toDate]);

  const filteredTotals = useMemo(() => {
    const cmaCount = filteredRows.filter((row) => row.document_type === 'CMA').length;
    const marketingCount = filteredRows.filter((row) => row.document_type === 'Marketing Plan').length;
    return {
      all: cmaCount + marketingCount,
      cma: cmaCount,
      marketing: marketingCount,
      loom: filteredRows.filter((row) => row.document_type === 'Loom Report').length,
      uniqueAgents: new Set(filteredRows.map((row) => row.agent_email.trim().toLowerCase()).filter(Boolean)).size,
    };
  }, [filteredRows]);

  const leaderboardRows = useMemo(() => {
    const map = new Map<string, { agentName: string; agentEmail: string; marketCenterName: string; total: number; cma: number; marketing: number }>();
    for (const row of filteredRows) {
      const key = row.agent_email.trim().toLowerCase() || row.agent_name.trim().toLowerCase();
      const existing = map.get(key) ?? {
        agentName: row.agent_name,
        agentEmail: row.agent_email,
        marketCenterName: row.market_center_name ?? '-',
        total: 0,
        cma: 0,
        marketing: 0,
      };
      if (existing.marketCenterName === '-' && row.market_center_name) {
        existing.marketCenterName = row.market_center_name;
      }
      if (row.document_type === 'CMA') {
        existing.cma += 1;
        existing.total += 1;
      }
      if (row.document_type === 'Marketing Plan') {
        existing.marketing += 1;
        existing.total += 1;
      }
      map.set(key, existing);
    }

    return Array.from(map.values())
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [filteredRows]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, dateFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  function changeFilter(next: 'ALL' | 'CMA' | 'Marketing Plan' | 'Loom Report'): void {
    setTypeFilter(next);
  }

  function exportFilteredCsv(): void {
    if (filteredRows.length === 0) return;

    const headers = [
      'Document Type',
      'Agent Name',
      'Agent Email',
      'Market Centre',
      'Property Address',
      'Seller Name',
      'Seller Email',
      'Seller Phone',
      'File Name',
      'File URL',
      'Created At',
    ];

    const lines = filteredRows.map((row) => [
      row.document_type,
      row.agent_name,
      row.agent_email,
      row.market_center_name ?? '',
      row.property_address ?? '',
      row.seller_name ?? '',
      row.seller_email ?? '',
      row.seller_phone ?? '',
      row.file_name,
      row.file_url,
      row.created_at,
    ].map((cell) => csvEscape(String(cell))).join(','));

    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `regional-ai-tools-${toYmd(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  if (!isRegionalAdmin) {
    return (
      <div className="surface-card p-5">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          This view is only available in Regional Admin context.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="page-title">Regional AI Document Library</h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          Consolidated visibility across CMAs, Marketing Plans, and Loom reports uploaded by agents.
        </p>
      </div>

      {!isLoading && !error && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label="Total Documents" value={filteredTotals.all} helper={`All-time: ${totals.all}`} />
          <SummaryCard label="CMAs" value={filteredTotals.cma} helper={`All-time: ${totals.cma}`} />
          <SummaryCard label="Marketing Plans" value={filteredTotals.marketing} helper={`All-time: ${totals.marketing}`} />
          <SummaryCard label="Agents Using Tool" value={filteredTotals.uniqueAgents} helper={`All-time: ${totals.uniqueAgents}`} />
        </div>
      )}

      {!isLoading && !error && (
        <div className="surface-card overflow-hidden">
          <div className="border-b px-5 py-4" style={{ borderColor: 'var(--border-soft)' }}>
            <button
              type="button"
              onClick={() => setShowLeaderboard((v) => !v)}
              className="flex w-full items-center justify-between text-left"
              aria-expanded={showLeaderboard}
            >
              <div>
                <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Top Agents by Usage
                </h2>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                  Top 10 agents based on current filters.
                </p>
              </div>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                {showLeaderboard ? 'Hide' : 'Show'}
              </span>
            </button>
          </div>

          {showLeaderboard && (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-soft)', background: 'var(--surface-strong)' }}>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Agent</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Market Centre</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Total (CMA + Marketing)</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>CMA</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Marketing</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                      No usage data for this filter.
                    </td>
                  </tr>
                )}
                {leaderboardRows.map((row, idx) => (
                  <tr key={`${row.agentEmail}-${idx}`} className="border-b" style={{ borderColor: 'var(--border-soft)' }}>
                    <td className="px-5 py-3">
                      <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{row.agentName}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{row.agentEmail}</div>
                    </td>
                    <td className="px-5 py-3" style={{ color: 'var(--text-muted)' }}>{row.marketCenterName}</td>
                    <td className="px-5 py-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{row.total}</td>
                    <td className="px-5 py-3" style={{ color: 'var(--text-muted)' }}>{row.cma}</td>
                    <td className="px-5 py-3" style={{ color: 'var(--text-muted)' }}>{row.marketing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      <div className="surface-card overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'var(--border-soft)' }}>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Regional Activity
            </h2>
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}>
              {filteredRows.length} filtered
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Date
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as DateFilter)}
              className="rounded-md border px-2.5 py-1.5 text-xs"
              style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)', background: 'var(--surface)' }}
            >
              <option value="ALL">All Time</option>
              <option value="THIS_MONTH">This Month</option>
              <option value="LAST_30_DAYS">Last 30 Days</option>
              <option value="CUSTOM">Custom Range</option>
            </select>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Filter
            </label>
            <select
              value={typeFilter}
              onChange={(e) => changeFilter(e.target.value as 'ALL' | 'CMA' | 'Marketing Plan' | 'Loom Report')}
              className="rounded-md border px-2.5 py-1.5 text-xs"
              style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)', background: 'var(--surface)' }}
            >
              <option value="ALL">All Types</option>
              <option value="CMA">CMA</option>
              <option value="Marketing Plan">Marketing Plan</option>
              <option value="Loom Report">Loom Report</option>
            </select>
            {dateFilter === 'CUSTOM' && (
              <>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rounded-md border px-2 py-1.5 text-xs"
                  style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)', background: 'var(--surface)' }}
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="rounded-md border px-2 py-1.5 text-xs"
                  style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)', background: 'var(--surface)' }}
                />
              </>
            )}
            <button
              type="button"
              onClick={exportFilteredCsv}
              disabled={filteredRows.length === 0}
              className="rounded-md border px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}
            >
              Export CSV
            </button>
          </div>
        </div>

        {isLoading && (
          <p className="px-5 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            Loading records...
          </p>
        )}

        {!!error && !isLoading && (
          <p className="px-5 py-8 text-center text-sm" style={{ color: '#B91C1C' }}>
            {(error as Error).message}
          </p>
        )}

        {!isLoading && !error && filteredRows.length === 0 && (
          <p className="px-5 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            No records found.
          </p>
        )}

        {!isLoading && !error && filteredRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-soft)', background: 'var(--surface-strong)' }}>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Type</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Agent</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Market Centre</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Address</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Seller Details</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Created</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Document</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className="border-b transition-colors hover:bg-slate-50"
                    style={{
                      borderColor: 'var(--border-soft)',
                      background: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-strong)',
                    }}
                  >
                    <td className="px-5 py-3"><TypePill type={row.document_type} /></td>
                    <td className="px-5 py-3">
                      <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{row.agent_name}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{row.agent_email}</div>
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {row.market_center_name ?? '-'}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-primary)' }}>
                      {row.property_address ?? '-'}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <div>{row.seller_name ?? 'Name: -'}</div>
                      <div>{row.seller_email ?? 'Email: -'}</div>
                      <div>{row.seller_phone ?? 'Phone: -'}</div>
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <a
                        href={row.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors hover:opacity-80"
                        style={{ background: 'var(--brand-soft)', color: 'var(--brand)', border: '1px solid var(--brand)' }}
                      >
                        Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between border-t px-5 py-3" style={{ borderColor: 'var(--border-soft)' }}>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredRows.length)} of {filteredRows.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-md border px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}
                >
                  Previous
                </button>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-md border px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, helper }: { label: string; value: number; helper?: string }) {
  return (
    <div className="surface-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
        {value}
      </p>
      {helper && (
        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          {helper}
        </p>
      )}
    </div>
  );
}
